import { useNavigate } from "react-router-dom";

// ============================================================================
//  TransformacionDigital — Hub de acceso a las herramientas del sistema.
//
//  Estética alineada a la app de gestión de DESEM: fondo gris muy claro,
//  tarjetas blancas redondeadas con sombra suave, ícono en chip de color
//  tenue, título en negro + subtítulo gris, y flecha a la derecha.
//
//  Navega con react-router. Ajustá las rutas si difieren de tu router.
// ============================================================================

// chips de color por herramienta (fondo tenue + ícono a tono), como la app
const CHIP_TEAL = { bg: "#E1F1F2", color: "#1B8A99" };
const CHIP_CORAL = { bg: "#FCECE7", color: "#E8613C" };
const CHIP_GRIS = { bg: "#EEF0F3", color: "#5B6B72" };

const HERRAMIENTAS = [
  {
    ruta: "/template-designer",
    titulo: "Diseñar formulario",
    sub: "Crear una hoja imprimible",
    chip: CHIP_TEAL,
    icono: IconoDiseñar,
  },
  {
    ruta: "/escaner-cuestionario",
    titulo: "Escanear hojas",
    sub: "Leer formularios en lote",
    chip: CHIP_CORAL,
    icono: IconoEscanear,
  },
  {
    ruta: "/revision-data-entry",
    titulo: "Validar datos",
    sub: "Revisar lo escrito a mano",
    chip: CHIP_TEAL,
    icono: IconoValidar,
  },
  {
    ruta: "/qr-scanner",
    titulo: "Check in QR",
    sub: "Escanea QR para registrar asistencia",
    chip: CHIP_GRIS,
    icono: IconoQr,
  },
  {
    ruta: "/chatbot",
    titulo: "ChatBot",
    sub: "Domino Mis Cuentas",
    chip: CHIP_GRIS,
    icono: IconoChat,
  },
];

export default function TransformacionDigital() {
  const navigate = useNavigate();

  return (
    <div style={S.pagina}>
      <div style={S.envoltura}>
        <header style={S.header}>
          <div style={S.marca}>DESEM Junior Achievement</div>
          <h1 style={S.h1}>Transformación digital</h1>
          <p style={S.intro}>
            Del papel a los datos. Diseñá el formulario, escaneá las respuestas
            y validá la información.
          </p>
        </header>

        <div style={S.grid}>
          {HERRAMIENTAS.map((h) => {
            const Icono = h.icono;
            return (
              <button
                key={h.ruta}
                style={S.card}
                onClick={() => navigate(h.ruta)}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <span style={{ ...S.chip, background: h.chip.bg, color: h.chip.color }}>
                  <Icono />
                </span>
                <span style={S.textos}>
                  <span style={S.titulo}>{h.titulo}</span>
                  <span style={S.sub}>{h.sub}</span>
                </span>
                <span style={S.flecha}>›</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function hoverOn(e) {
  e.currentTarget.style.transform = "translateY(-2px)";
  e.currentTarget.style.boxShadow = "0 10px 30px rgba(43,70,90,.10)";
}
function hoverOff(e) {
  e.currentTarget.style.transform = "translateY(0)";
  e.currentTarget.style.boxShadow = "0 4px 18px rgba(43,70,90,.06)";
}

// ---------------------------------------------------------------------------
//  íconos (trazo fino, heredan color del chip)
// ---------------------------------------------------------------------------
function base(children) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}
function IconoDiseñar() {
  return base(<>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <circle cx="8" cy="13" r="1.2" /><line x1="11" y1="13" x2="16" y2="13" />
    <circle cx="8" cy="17" r="1.2" /><line x1="11" y1="17" x2="16" y2="17" />
  </>);
}
function IconoEscanear() {
  return base(<>
    <path d="M4 8V5a1 1 0 0 1 1-1h3" /><path d="M16 4h3a1 1 0 0 1 1 1v3" />
    <path d="M20 16v3a1 1 0 0 1-1 1h-3" /><path d="M8 20H5a1 1 0 0 1-1-1v-3" />
    <line x1="4" y1="12" x2="20" y2="12" />
  </>);
}
function IconoValidar() {
  return base(<>
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </>);
}
function IconoQr() {
  return base(<>
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3" /><path d="M20 14v7" /><path d="M17 20h4" />
  </>);
}
function IconoChat() {
  return base(<>
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8 8.38 8.38 0 0 1 8.5-8.5 8.5 8.5 0 0 1 8.5 8.5z" />
  </>);
}

// ---------------------------------------------------------------------------
//  estilos (piel de la app: gris claro + tarjetas blancas redondeadas)
// ---------------------------------------------------------------------------
const S = {
  pagina: {
    minHeight: "100vh", background: "#F0F2F5",
    fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,sans-serif",
    color: "#1a2b31", padding: "44px 28px 60px",
  },
  envoltura: { maxWidth: 1180, margin: "0 auto" },

  header: { marginBottom: 34 },
  marca: {
    fontSize: 12, fontWeight: 700, color: "#1B8A99", letterSpacing: ".06em",
    textTransform: "uppercase", marginBottom: 10,
  },
  h1: { fontSize: 34, fontWeight: 800, margin: 0, letterSpacing: "-.02em", color: "#1c3d47" },
  intro: { fontSize: 15, color: "#7b8a90", lineHeight: 1.55, marginTop: 12, maxWidth: 560 },

  grid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 18,
  },
  card: {
    display: "flex", alignItems: "center", gap: 18, textAlign: "left", cursor: "pointer",
    background: "#fff", border: "none", borderRadius: 22, padding: "24px 26px",
    boxShadow: "0 4px 18px rgba(43,70,90,.06)",
    transition: "transform .18s ease, box-shadow .18s ease",
  },
  chip: {
    width: 56, height: 56, borderRadius: 16, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  textos: { display: "flex", flexDirection: "column", flex: 1, minWidth: 0 },
  titulo: { fontSize: 19, fontWeight: 700, color: "#1c2e35", letterSpacing: "-.01em" },
  sub: { fontSize: 14, fontWeight: 600, color: "#aeb8bd", marginTop: 3 },
  flecha: { fontSize: 26, color: "#cdd5d9", fontWeight: 400, lineHeight: 1 },
};